import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { UsersIcon, BuildingIcon, ShieldIcon, WalletIcon, ChevronRightIcon, FileIcon, ClockIcon, BellIcon } from '../components/Icons';

const NAT_COLORS = ['#fb866d','#1a2744','#22c55e','#f59e0b','#8b5cf6','#06b6d4','#ec4899','#84cc16'];

// Time-of-day greeting in Jeddah time (Asia/Riyadh), personalised from the login
function greeting() {
  let first = '';
  try {
    const u = JSON.parse(localStorage.getItem('hr_user') || 'null');
    if (u?.name) first = u.name.split(' ')[0];
  } catch { /* fall through to generic */ }
  const hour = Number(new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone: 'Asia/Riyadh' }).format(new Date()));
  const part = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening';
  return first ? `${part}, ${first}` : part;
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get('/employees/stats'), api.get('/employees/alerts')])
      .then(([s, a]) => { setStats(s.data); setAlerts(a.data.slice(0, 6)); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-[60vh] text-slate-400 text-sm gap-3">
      <div className="w-5 h-5 border-2 border-slate-200 border-t-[#fb866d] rounded-full animate-spin" />
      Loading dashboard...
    </div>
  );

  const maxP = stats?.projectBreakdown?.[0]?.[1] || 1;
  const maxN = stats?.nationalityBreakdown?.[0]?.[1] || 1;
  const dot = { expired: 'bg-red-500', critical: 'bg-[#fb866d]', warning: 'bg-amber-400' };
  const badge = { expired: 'bg-red-50 text-red-600', critical: 'bg-orange-50 text-[#e8614a]', warning: 'bg-amber-50 text-amber-700' };

  const heroStats = [
    { icon: UsersIcon, val: stats?.active ?? 0, label: 'Employees', sub: 'active' },
    { icon: BuildingIcon, val: stats?.clients ?? 0, label: 'Clients', sub: 'active sites' },
    { icon: WalletIcon, val: `${((stats?.totalPayroll ?? 0) / 1000).toFixed(0)}K`, label: 'Payroll', sub: 'SAR / month' },
    { icon: WalletIcon, val: `${(stats?.totalDeductions ?? 0).toLocaleString()}`, label: 'Deductions', sub: 'SAR due', accent: (stats?.totalDeductions ?? 0) > 0 },
  ];

  const compliance = stats?.complianceByType || {};
  const complianceStats = [
    { icon: ShieldIcon, val: compliance.iqama ?? 0, label: 'Iqama', link: true },
    { icon: FileIcon, val: compliance.qiwa ?? 0, label: 'Qiwa', link: true },
    { icon: FileIcon, val: compliance.passport ?? 0, label: 'Passport', link: true },
    { icon: BellIcon, val: compliance.insurance ?? 0, label: 'Insurance', link: true },
    { icon: ClockIcon, val: compliance.leave ?? 0, label: 'Leave', link: true },
    { icon: BuildingIcon, val: compliance.contracts ?? 0, label: 'Contracts', link: true },
  ].map(t => ({ ...t, sub: t.val > 0 ? 'alerts' : 'clear', accent: t.val > 0 }));

  const allTiles = [...heroStats, ...complianceStats];

  return (
    <div>
      {/* Topbar */}
      <div className="hidden lg:flex bg-white border-b border-slate-100 px-8 py-4 items-center justify-between sticky top-0 z-40">
        <h1 className="text-lg font-bold text-slate-800">Dashboard</h1>
        <div className="flex items-center gap-3">
          <span className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-xs font-semibold text-slate-600">Admin</span>
        </div>
      </div>

      <div className="p-4 lg:p-8 stagger">
        {/* Hero banner */}
        <div className="rounded-3xl p-5 lg:p-7 mb-6 relative overflow-hidden"
          style={{
            background: 'linear-gradient(125deg, #0a1428 0%, #101d3a 40%, #1c2c55 75%, #24345f 100%)',
            boxShadow: '0 20px 50px -18px rgba(10,20,40,0.55), inset 0 1px 0 rgba(255,255,255,0.08)',
          }}>
          {/* aurora glows */}
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(600px 260px at 80% 15%, rgba(251,134,109,0.28) 0%, transparent 60%)' }} />
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(500px 240px at 12% 90%, rgba(96,140,255,0.18) 0%, transparent 60%)' }} />
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(380px 200px at 45% -10%, rgba(45,212,191,0.10) 0%, transparent 65%)' }} />
          {/* faint grid texture */}
          <div className="absolute inset-0 pointer-events-none opacity-[0.05]"
            style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '44px 44px' }} />
          {/* top shine */}
          <div className="absolute top-0 left-8 right-8 h-px pointer-events-none"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)' }} />
          <div className="relative flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl lg:text-[22px] font-bold tracking-tight"
                style={{ background: 'linear-gradient(92deg, #ffffff 0%, #ffd9cf 55%, #fb866d 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                {greeting()}
              </h2>
              <p className="text-slate-400 text-[13px] mt-1 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" style={{ boxShadow: '0 0 8px rgba(52,211,153,0.9)' }} />
                Delta Manpower HR Console · {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
          <div className="relative grid grid-cols-3 sm:grid-cols-5 xl:grid-cols-10 gap-2 lg:gap-2.5">
            {allTiles.map(({ icon: Icon, val, label, sub, accent, link }) => (
              <button key={label} onClick={() => link && navigate('/compliance')}
                className={`group rounded-2xl px-3 py-3.5 backdrop-blur-md text-left transition-all duration-300 ${link ? 'hover:-translate-y-1 cursor-pointer' : 'cursor-default'}`}
                style={{
                  background: accent
                    ? 'linear-gradient(160deg, rgba(251,134,109,0.16) 0%, rgba(251,134,109,0.05) 100%)'
                    : 'linear-gradient(160deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 100%)',
                  border: `1px solid ${accent ? 'rgba(251,134,109,0.40)' : 'rgba(255,255,255,0.10)'}`,
                  boxShadow: accent ? '0 4px 20px -6px rgba(251,134,109,0.25), inset 0 1px 0 rgba(255,255,255,0.10)' : 'inset 0 1px 0 rgba(255,255,255,0.08)',
                }}
                onMouseEnter={e => { if (link) e.currentTarget.style.boxShadow = accent ? '0 10px 28px -6px rgba(251,134,109,0.45), inset 0 1px 0 rgba(255,255,255,0.14)' : '0 10px 24px -8px rgba(120,150,255,0.35), inset 0 1px 0 rgba(255,255,255,0.14)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = accent ? '0 4px 20px -6px rgba(251,134,109,0.25), inset 0 1px 0 rgba(255,255,255,0.10)' : 'inset 0 1px 0 rgba(255,255,255,0.08)'; }}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Icon size={19} className={`${accent ? 'text-[#fb866d]' : 'text-slate-400'} transition-transform duration-300 group-hover:scale-110`} />
                  <span className={`text-[21px] font-bold leading-none tracking-tight ${accent ? '' : 'text-white'}`}
                    style={accent ? { background: 'linear-gradient(120deg, #ffb4a0, #fb866d)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' } : undefined}>
                    {val}
                  </span>
                </div>
                <p className="text-white/90 text-[11px] font-semibold leading-tight whitespace-nowrap">{label}</p>
                <p className={`text-[9.5px] mt-0.5 ${accent ? 'text-[#fb866d]/80' : 'text-slate-500'}`}>{sub}</p>
                {link && <div className="h-0.5 mt-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: 'linear-gradient(90deg, #fb866d, transparent)' }} />}
              </button>
            ))}
          </div>
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          <div className="bg-white rounded-2xl border border-slate-100 p-6 transition-all duration-300 hover:-translate-y-0.5" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.05), 0 8px 24px -12px rgba(15,27,51,0.10)' }} onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 6px rgba(15,27,51,0.06), 0 16px 36px -12px rgba(232,97,74,0.18)'} onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(15,27,51,0.05), 0 8px 24px -12px rgba(15,27,51,0.10)'}>
            <p className="text-[14px] font-bold text-slate-800 mb-1 flex items-center gap-2"><span className="inline-block w-1 h-4 rounded-full" style={{ background: 'linear-gradient(180deg, #fb866d, #e8614a)' }} />Headcount by Client Site</p>
            <p className="text-xs text-slate-400 mb-5">Active employees per project</p>
            <div className="space-y-3">
              {(stats?.projectBreakdown || []).slice(0, 8).map(([proj, count]) => (
                <div key={proj} className="flex items-center gap-3">
                  <span className="text-[12.5px] text-slate-500 w-32 truncate flex-shrink-0" title={proj}>{proj}</span>
                  <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${(count / maxP) * 100}%`, background: 'linear-gradient(90deg, #ffb4a0, #fb866d 55%, #e8614a)', boxShadow: '0 0 10px rgba(251,134,109,0.45)' }} />
                  </div>
                  <span className="text-[11.5px] font-bold text-slate-700 min-w-[30px] text-center px-1.5 py-0.5 rounded-md bg-slate-50 border border-slate-100">{count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-6 transition-all duration-300 hover:-translate-y-0.5" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.05), 0 8px 24px -12px rgba(15,27,51,0.10)' }} onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 6px rgba(15,27,51,0.06), 0 16px 36px -12px rgba(232,97,74,0.18)'} onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(15,27,51,0.05), 0 8px 24px -12px rgba(15,27,51,0.10)'}>
            <p className="text-[14px] font-bold text-slate-800 mb-1 flex items-center gap-2"><span className="inline-block w-1 h-4 rounded-full" style={{ background: 'linear-gradient(180deg, #fb866d, #e8614a)' }} />Nationality Breakdown</p>
            <p className="text-xs text-slate-400 mb-5">Active workforce composition</p>
            <div className="space-y-3">
              {(stats?.nationalityBreakdown || []).slice(0, 7).map(([nat, count], i) => (
                <div key={nat} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: NAT_COLORS[i % NAT_COLORS.length] }} />
                  <span className="text-[12.5px] text-slate-500 w-28 truncate flex-shrink-0">{nat}</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(count / maxN) * 100}%`, background: NAT_COLORS[i % NAT_COLORS.length], boxShadow: `0 0 10px ${NAT_COLORS[i % NAT_COLORS.length]}55` }} />
                  </div>
                  <span className="text-[12.5px] font-bold text-slate-700 w-7 text-right">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Secondary row: sponsorship + alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="bg-white rounded-2xl border border-slate-100 p-6 transition-all duration-300 hover:-translate-y-0.5" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.05), 0 8px 24px -12px rgba(15,27,51,0.10)' }} onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 6px rgba(15,27,51,0.06), 0 16px 36px -12px rgba(232,97,74,0.18)'} onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(15,27,51,0.05), 0 8px 24px -12px rgba(15,27,51,0.10)'}>
            <p className="text-[14px] font-bold text-slate-800 mb-1 flex items-center gap-2"><span className="inline-block w-1 h-4 rounded-full" style={{ background: 'linear-gradient(180deg, #fb866d, #e8614a)' }} />By Sponsorship</p>
            <p className="text-xs text-slate-400 mb-5">Active employees</p>
            <div className="space-y-3.5">
              {(stats?.sponsorshipBreakdown || []).slice(0, 6).map(([sp, count], i) => (
                <div key={sp} className="flex items-center justify-between px-3 py-2 -mx-3 rounded-xl transition-colors hover:bg-slate-50">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: NAT_COLORS[i % NAT_COLORS.length], boxShadow: `0 0 8px ${NAT_COLORS[i % NAT_COLORS.length]}66` }} />
                    <span className="text-[13px] text-slate-600 truncate">{sp}</span>
                  </div>
                  <span className="text-[12px] font-bold text-slate-700 px-2 py-0.5 rounded-md bg-slate-50 border border-slate-100">{count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.05), 0 8px 24px -12px rgba(15,27,51,0.10)' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-50">
              <div>
                <p className="text-[14px] font-bold text-slate-800 flex items-center gap-2"><span className="inline-block w-1 h-4 rounded-full" style={{ background: 'linear-gradient(180deg, #fb866d, #e8614a)' }} />Document Expiry Alerts</p>
                <p className="text-xs text-slate-400 mt-0.5">Iqama · Qiwa · Passport — most urgent first</p>
              </div>
              <button onClick={() => navigate('/compliance')}
                className="flex items-center gap-1 text-xs font-semibold text-[#e8614a] px-3.5 py-2 rounded-full border border-[#fb866d]/30 hover:bg-[#fde8e3] transition-all">
                View All <ChevronRightIcon size={14} />
              </button>
            </div>
            {alerts.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm">No pending alerts</div>
            ) : alerts.map(e => (
              <div key={`${e.id}-${e.alertType}`} className="relative flex items-center gap-3 px-6 py-3 border-b border-slate-50 last:border-0 hover:bg-[#fff7f5] transition-colors group">
                <div className={`absolute left-0 top-0 bottom-0 w-[3px] opacity-0 group-hover:opacity-100 transition-opacity ${e.level === 'expired' || e.level === 'critical' ? 'bg-red-400' : 'bg-amber-400'}`} />
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${dot[e.level]}`} />
                <span className="text-[13px] font-medium text-slate-800 flex-1 truncate">{e.name}</span>
                <span className="text-xs text-slate-400 hidden md:block">{e.project}</span>
                <span className="text-xs text-slate-400 hidden lg:block">{e.sponsorship || '—'}</span>
                <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{e.alertType}</span>
                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${badge[e.level]}`}>
                  {e.days < 0 ? `Expired ${Math.abs(e.days)}d ago` : `${e.days}d left`}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}